import styled, { css } from 'styled-components';
import type React from 'react';

export const AutoCompleteRoot = styled.div<{ isFullScreen: boolean }>`
  width: 100%;
  ${({ isFullScreen }) =>
    !isFullScreen &&
    css`
      position: relative;
    `};
`;

export const AutoCompleteContainer = styled.div<{ isFullScreen: boolean }>`
  width: 100%;
  position: relative;
  ${({ isFullScreen }) =>
    isFullScreen &&
    css`
      background-color: #fff;
      height: 68px;
    `};

  * {
    box-sizing: border-box;
  }
`;

// The evo design's 20px icon sits 16px in; the current 19px one is centred on the same point.
const ICON_INSET = { evo: 16, current: 16.5 };

export const AutoCompleteInner = styled.div<{ isFullScreen: boolean; focus: boolean; $evo?: boolean }>`
  width: 100%;
  position: relative;
  background-color: ${({ theme, $evo }) =>
    $evo ? theme.kallisto.color.background.static.backgroundLayer01 : theme.kallisto.color.common.black[50]};
  display: flex;
  height: 52px;
  border-radius: 8px;

  > img {
    flex-shrink: 0;
    position: absolute;
    top: ${({ $evo }) => ($evo ? ICON_INSET.evo : ICON_INSET.current)}px;
    left: ${({ $evo }) => ($evo ? ICON_INSET.evo : ICON_INSET.current)}px;
  }

  ${({ isFullScreen }) =>
    isFullScreen &&
    css`
      padding-bottom: 16px;
      box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.largeDownShadow};
      width: calc(100% - 32px);
      margin: 0 auto;

      input {
        margin: auto;
        display: block;
      }
    `};

  ${({ focus }) =>
    focus
      ? css`
          box-shadow: inset 0 0 0 1px ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
        `
      : css`
          &:hover {
            box-shadow: inset 0 0 0 3px ${({ theme }) => theme.kallisto.color.common.black[200]};
          }
        `}
`;

const baseSuggestionStyles = css`
  background: white;
  width: 100%;
  border-radius: 16px;
`;

export const NoSuggestions = styled.div<{ isFullScreen: boolean }>`
  ${baseSuggestionStyles};
  background: white;
  box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.largeDownShadow};
  border-radius: 16px;
  width: 100%;
  height: 52px;
  position: absolute;
  top: 84px;
  left: 0;
  right: 0;
  padding: 16px;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.kallisto.color.common.semantic.error[500]};
  z-index: 1;

  ${({ isFullScreen }) =>
    isFullScreen &&
    css`
      width: calc(100% - 32px);
      top: 138px;
      left: 16px;
      right: unset;
      background: ${({ theme }) => theme.kallisto.color.background.static.backgroundErrorSubtle};
      outline: 2px solid ${({ theme }) => theme.kallisto.color.common.semantic.error[500]};
      outline-offset: -2px;
      border-radius: 16px;
    `}
`;

export const SuggestionsLabel = styled.label<{ isFullScreen: boolean; focus: boolean }>`
  position: absolute;
  left: 44px;
  font-style: normal;
  font-weight: 400;
  transition: all 0.2s ease-in-out;
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  ${({ theme }) => theme.kallisto.mixins.font('Body')};

  ${({ focus }) =>
    focus
      ? css`
          height: 16px;
          white-space: nowrap;
          font-size: ${({ theme }) => theme.kallisto.font.size['150']};
        `
      : css`
          display: flex;
          align-items: center;
          height: 52px;
        `}
`;

export const SuggestionInput = styled.input<{
  isFullScreen: boolean;
  value: string;
  focus: boolean;
  ref: React.MutableRefObject<HTMLInputElement | undefined>;
}>`
  display: block;
  border: none;
  border-radius: 8px;
  height: 52px;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  width: 100%;
  padding: 20px 44px 6px 44px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  background-color: transparent;

  ${({ value }) =>
    value.length > 0 &&
    css`
      padding-right: 44px;
    `}
`;

export const SuggestionsContainerOuter = styled.div<{ isFullScreen: boolean }>`
  ${baseSuggestionStyles};
  box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.largeDownShadow};
  position: absolute;
  top: 84px;
  left: 0;
  overflow: hidden;
  z-index: 1;

  ${({ isFullScreen }) =>
    isFullScreen &&
    css`
      width: calc(100% - 32px);
      left: 16px;
      top: 143px;
    `}
`;

export const SuggestionsContainer = styled.ul<{ isFullScreen: boolean }>`
  max-height: 256px;
  overflow: auto;

  ${({ isFullScreen }) =>
    isFullScreen &&
    css`
      max-height: calc(100vh - 161px);
    `}
`;

export const SuggestionContainer = styled.li`
  &.suggestion-active,
  &:hover {
    background-color: ${({ theme }) => theme.kallisto.color.common.black[50]};
    cursor: pointer;
  }
`;

export const AutocompleteTagDelete = styled.button<{ isFullScreen: boolean }>`
  position: absolute;
  top: 25px;
  transform: translateY(-50%);
  right: 16px;
  height: 16px;
  z-index: 22;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;

  [data-library-icon] {
    color: ${({ theme }) => theme.kallisto.color.common.black[200]};
    transition: color 0.2s ease;
  }

  &:hover {
    [data-library-icon] {
      color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
    }
  }
`;
